import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Pagination from '@mui/material/Pagination';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import {
  IoCheckmarkCircle, IoWarning, IoCloseCircle,
  IoOpenOutline, IoSearchOutline, IoRefreshOutline,
} from 'react-icons/io5';

// ─── Score helpers ────────────────────────────────────────────────────────────

const SCORE_CONFIG = {
  pass: { label: 'Perfect',      color: 'success', icon: <IoCheckmarkCircle color="#2e7d32" /> },
  warn: { label: 'Needs work',   color: 'warning', icon: <IoWarning color="#ed6c02" /> },
  fail: { label: 'Missing / Bad',color: 'error',   icon: <IoCloseCircle color="#d32f2f" /> },
};

function ScoreChip({ score }) {
  const cfg = SCORE_CONFIG[score] || SCORE_CONFIG.fail;
  return (
    <Chip
      size="small"
      color={cfg.color}
      label={cfg.label}
      icon={cfg.icon}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.72rem' }}
    />
  );
}

function FieldCell({ value, score }) {
  const isEmpty = !value;
  const len = value ? value.length : 0;
  const borderColor = score.score === 'pass' ? '#2e7d32' : score.score === 'warn' ? '#ed6c02' : '#d32f2f';

  return (
    <Box sx={{ borderLeft: `3px solid ${borderColor}`, pl: 1 }}>
      {isEmpty ? (
        <Typography variant="caption" color="error.main" sx={{ fontStyle: 'italic' }}>
          — empty —
        </Typography>
      ) : (
        <>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3, color: 'text.primary' }}>
            {value.length > 80 ? value.slice(0, 80) + '…' : value}
          </Typography>
          <Typography variant="caption" color={score.score === 'pass' ? 'success.main' : score.score === 'warn' ? 'warning.main' : 'error.main'} sx={{ fontSize: '0.68rem' }}>
            {len} chars{score.issue ? ` · ${score.issue}` : ''}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, color, subtitle }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 120 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="h4" fontWeight={700} color={`${color}.main`}>{value ?? '—'}</Typography>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminSeoChecker() {
  const navigate = useNavigate();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filter, setFilter]     = useState('all');   // all | issues | pass
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);

  const fetchData = useCallback(async (pg, flt, srch) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: pg, limit: 50, filter: flt });
      if (srch) params.set('search', srch);
      const res = await api(`/api/admin/products/seo-check?${params}`);
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to load SEO data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page, filter, search); }, [fetchData, page, filter, search]);

  const handleFilter = (_, val) => {
    if (!val) return;
    setFilter(val);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 400);
  };

  const handleEdit = (id) => navigate(`/admin/products/edit/${id}`);

  const summary = data?.summary;
  const products = data?.products || [];
  const pagination = data?.pagination;

  const passPercent = summary ? Math.round((summary.pass / summary.total) * 100) : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Product SEO Checker</Typography>
        <Typography variant="body2" color="text.secondary">
          Analyse SEO title, meta description and focus keyword for all {summary?.total ?? '…'} products.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      {summary && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <SummaryCard label="Total Products" value={summary.total} color="info" />
          <SummaryCard label="Perfect SEO" value={summary.pass} color="success" subtitle={`${passPercent}% of total`} />
          <SummaryCard label="Needs Improvement" value={summary.warn} color="warning" />
          <SummaryCard label="Missing / Broken" value={summary.fail} color="error" />
        </Box>
      )}

      {/* Health bar */}
      {summary && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">SEO Health</Typography>
            <Typography variant="caption" fontWeight={700} color={passPercent >= 80 ? 'success.main' : passPercent >= 50 ? 'warning.main' : 'error.main'}>
              {passPercent}% perfect
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={passPercent}
            color={passPercent >= 80 ? 'success' : passPercent >= 50 ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" value={filter} exclusive onChange={handleFilter}>
          <ToggleButton value="all">All {summary ? `(${summary.total})` : ''}</ToggleButton>
          <ToggleButton value="issues" sx={{ color: 'warning.main' }}>
            Issues {summary ? `(${summary.warn + summary.fail})` : ''}
          </ToggleButton>
          <ToggleButton value="pass" sx={{ color: 'success.main' }}>
            Perfect {summary ? `(${summary.pass})` : ''}
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Search product name…"
          value={searchInput}
          onChange={handleSearchChange}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><IoSearchOutline /></InputAdornment>
            ),
          }}
        />

        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchData(page, filter, search)} size="small">
            <IoRefreshOutline />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Table */}
      <Card variant="outlined">
        {loading && <LinearProgress />}
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell width={40}>#</TableCell>
                <TableCell width={220}><Typography variant="caption" fontWeight={700}>Product</Typography></TableCell>
                <TableCell width={200}><Typography variant="caption" fontWeight={700}>SEO Title</Typography></TableCell>
                <TableCell width={240}><Typography variant="caption" fontWeight={700}>Meta Description</Typography></TableCell>
                <TableCell width={160}><Typography variant="caption" fontWeight={700}>Focus Keyword</Typography></TableCell>
                <TableCell width={110}><Typography variant="caption" fontWeight={700}>Overall</Typography></TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && products.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No products match.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {products.map((p) => (
                <TableRow
                  key={p.id}
                  hover
                  sx={{
                    verticalAlign: 'top',
                    borderLeft: `3px solid ${p.overall === 'pass' ? '#2e7d32' : p.overall === 'warn' ? '#ed6c02' : '#d32f2f'}`,
                  }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>{p.id}</TableCell>

                  <TableCell>
                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>
                      {p.name?.slice(0, 60)}{p.name?.length > 60 ? '…' : ''}
                    </Typography>
                    {p.category && (
                      <Chip label={p.category} size="small" variant="outlined" sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />
                    )}
                  </TableCell>

                  <TableCell>
                    <FieldCell value={p.seo_title} score={p.title_score} />
                  </TableCell>

                  <TableCell>
                    <FieldCell value={p.seo_desc} score={p.desc_score} />
                  </TableCell>

                  <TableCell>
                    <FieldCell value={p.keyword} score={p.kw_score} />
                  </TableCell>

                  <TableCell>
                    <ScoreChip score={p.overall} />
                  </TableCell>

                  <TableCell>
                    <Tooltip title="Edit product SEO">
                      <IconButton size="small" onClick={() => handleEdit(p.id)}>
                        <IoOpenOutline size={15} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Card>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        {Object.entries(SCORE_CONFIG).map(([key, cfg]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {cfg.icon}
            <Typography variant="caption" color="text.secondary">
              <strong>{cfg.label}</strong>
              {key === 'pass' && ' — Title 40–65 chars, Desc 120–160 chars, Keyword present'}
              {key === 'warn' && ' — Length slightly off, still functional'}
              {key === 'fail' && ' — Missing or too short, needs immediate fix'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
